function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/**
 * Behaviors menu: list of saved Behaviors to choose from
 */
import * as React from 'react';
import clsx from 'clsx';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { InputComponent } from 'common/components/input';
import { ItemsMenuReact } from 'common/components/items-menu';
import { ItemClickEvent, Section, SectionItem } from 'common/components/items-menu/types';
import { SkeletonListReact } from 'common/components/skeleton-list';
import { SkeletonListRowSize } from 'common/components/skeleton-list/types';
import { withStyles } from 'common/util/react';
import { ContextPanelData } from 'common/widgets/context-panel/types';
import { FeatureGate, Permission } from 'common/report/constants';
import { BehaviorEntity as Behavior } from 'app_api/projects/behaviors/types';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { getProjectId, getWorkspaceId } from 'common/report/util';
import { ContextPanelReact } from 'common/widgets/context-panel';
import { IronContextRegistry } from 'common/report/context-registry';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useTranslation } from 'common/i18n/hooks';
import { MetricType } from 'common/types/reports/bookmark';
import { hasFirstClassMetrics } from 'apps/lexicon/src/lexicon-app/metrics/utils';
import { ChildMenuInterface, ChildMenuItemsCache, ContextPanelRenderer } from '../child-menu';
import { MenuSearchBar } from '../mixins/search-bar';
import { getChildMenuHeight } from '../utils/util';
import css from './index.module.styl?inline';
import styles from './index.module.styl';
import { createContextPanelEventHandlers, generateBehaviorContextPanelData } from './context-panel';
import { getSavedBehaviorSections } from './utils';
interface BehaviorsMenuState {
  searchFilter: string;
}
interface BehaviorsMenuProps {
  keydownProxyEl?: HTMLElement;
  onSetKeydownProxyEl?: (el: HTMLElement) => void;
  selected: Selected;
  metricType?: MetricType;
  open?: boolean;
  eventNames?: string[];
  projectId?: number;
  readonly?: boolean;
  trackName?: string;
  userId?: number;
  workspaceId?: number;
  onSelect: (ev: any) => void;
  onCreate: (ev?: any) => void;
  onRender?: () => void;
}
type Selected = {
  id: number;
  name: string;
};
const RECENT_ITEMS_SUB_KEYS = {
  [MetricType.Funnel]: "funnel",
  [MetricType.Retention]: "retention"
};
export class BehaviorsMenu extends React.PureComponent<BehaviorsMenuProps & Pick<IronContextRegistry, `currentProject` | `behaviors` | `i18n`>, BehaviorsMenuState> implements ChildMenuInterface {
  constructor(props) {
    var _userCookieUtils$getU;
    super(props);
    _defineProperty(this, "searchInputEl", null);
    _defineProperty(this, "_cache", null);
    _defineProperty(this, "childMenuRef", React.createRef<HTMLDivElement>());
    _defineProperty(this, "state", {
      searchFilter: ""
    });
    this._cache = ChildMenuItemsCache.createCache(props.userId || ((_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id), Number(props.projectId || getProjectId()), "behaviors-menu", Number(props.workspaceId || getWorkspaceId()));
  }
  componentDidUpdate() {
    var _this$props$onRender, _this$props;
    (_this$props$onRender = (_this$props = this.props).onRender) === null || _this$props$onRender === void 0 || _this$props$onRender.call(_this$props);
  }
  getRecentBehaviors(type) {
    return this._cache.getRecentItems(RECENT_ITEMS_SUB_KEYS[type], 3) || [];
  }
  addRecentBehavior(type, behavior) {
    this._cache.addRecentItem(RECENT_ITEMS_SUB_KEYS[type], behavior, item => item.id);
  }
  canShare() {
    var _this$props$currentPr;
    return (_this$props$currentPr = this.props.currentProject.get()) === null || _this$props$currentPr === void 0 || (_this$props$currentPr = _this$props$currentPr.value) === null || _this$props$currentPr === void 0 ? void 0 : _this$props$currentPr.getFeatureGate(FeatureGate.Sharing);
  }
  canWriteBehaviors() {
    var _this$props$currentPr2;
    return (_this$props$currentPr2 = this.props.currentProject.get()) === null || _this$props$currentPr2 === void 0 || (_this$props$currentPr2 = _this$props$currentPr2.value) === null || _this$props$currentPr2 === void 0 ? void 0 : _this$props$currentPr2.hasPermission(Permission.WriteBehaviors);
  }
  handleClickItem(_ref: ItemClickEvent<Behavior>) {
    let {
      detail
    }: ItemClickEvent<Behavior> = _ref;
    const {
      item
    } = detail;
    if (item.value.id) {
      this.addRecentBehavior(this.props.metricType, item.value);
    }
    this.props.onSelect({
      detail: {
        item: item.value
      }
    });
  }
  getSections(): Array<Section<Behavior>> {
    const firstClassMetricsEnabled = hasFirstClassMetrics(this.props.currentProject);
    const metricType = this.props.metricType;
    const recentBehaviorMenuItems = this.getRecentBehaviors(metricType);
    const savedBehaviors = this.props.behaviors.getAllValues()
    // fresh deleted behaviors result in `null` in lexicon
    .filter(Boolean).filter(metric => metric.type === metricType && metric.can_view);
    const sections = getSavedBehaviorSections({
      firstClassMetricsEnabled,
      metricType: metricType === MetricType.Funnel ? "funnel" : "retention",
      recentBehaviorMenuItems,
      savedBehaviors
    });
    return sections;
  }
  selectedLabel() {
    const selected = this.props.selected;
    return selected.name;
  }
  canOpenMenu() {
    return !this.props.readonly;
  }
  shouldCloseOnSelect() {
    return true;
  }
  getSelectedHoverTooltipData() {
    var _this$props$selected;
    return this.getTooltipData((_this$props$selected = this.props.selected) === null || _this$props$selected === void 0 ? void 0 : _this$props$selected.id);
  }
  getTooltipData(id: number): ContextPanelData {
    return generateBehaviorContextPanelData({
      behaviorsCtx: this.props.behaviors,
      canShare: this.canShare(),
      canWriteBehaviors: this.canWriteBehaviors(),
      firstClassMetricsEnabled: hasFirstClassMetrics(this.props.currentProject),
      id
    });
  }
  render(this: BehaviorsMenu) {
    const {
      t
    } = this.props.i18n;
    const isLoading = this.props.behaviors.isLoadingAll();
    const contextPanelEventHandlers = createContextPanelEventHandlers({
      availableContexts: {
        behaviors: this.props.behaviors
      }
    });
    return <div className={clsx(styles.mpBehaviorsMenu, "mp-behaviors-menu", "mp-child-menu")} ref={this.childMenuRef} style={{
      height: getChildMenuHeight()
    }} data-sentry-component="BehaviorsMenu" data-sentry-source-file="index.tsx">
        {this.props.open && <>
            <MenuSearchBar ref={el => this.props.onSetKeydownProxyEl(el)} value={this.state.searchFilter} handleSearchInput={value => this.setState({
          searchFilter: value
        })} placeholder='Search...' className={styles.searchWrapper}>
              <ButtonReact className={styles.createCustomButton} theme={ButtonTheme.Tertiary} icon={"plus"} onClick={this.props.onCreate}>
                {t("query-builder.create")}
              </ButtonReact>
            </MenuSearchBar>
            {isLoading ? <SkeletonListReact className={styles.mpBehaviorsMenuSkeletonList} numRows={6} rowSize={SkeletonListRowSize.Large} /> : <ItemsMenuReact className={styles.mpBehaviorsMenuItemsMenu} elref={"mainSelectionMenu"} emptyMessage={t("query-builder.no-items")} multiple={false} searchFilter={this.state.searchFilter} disableSearchSort disableSearchFilterMatchingParts disableFirstItemPadding onClick={ev => ev.stopPropagation()} onClickItem={ev => this.handleClickItem(ev)} onHoverIn={contextPanelEventHandlers.onHoverIn} onHoverOut={contextPanelEventHandlers.onHoverOut} onTooltipContextAction={contextPanelEventHandlers.onTooltipContextAction} onTooltipContextEditDescription={contextPanelEventHandlers.onTooltipContextEditDescription} sections={this.getSections()} keydownProxyEl={this.props.keydownProxyEl} contextPanelTransform={(item: SectionItem<Behavior>) => {
          return item.value.id ? this.getTooltipData(item.value.id) : null;
        }} contextPanelPositionParentElement={this.childMenuRef.current} />}
          </>}
      </div>;
  }
}
_defineProperty(BehaviorsMenu, "contextPanelRenderer", params => {
  if (!params.tooltipData) {
    return null;
  }
  return <ContextPanelReact {...params.contextPanelProps} {...createContextPanelEventHandlers(params)} data={params.tooltipData} elref='contextPanel' />;
});
export const BehaviorsMenuReact = withStyles(React.forwardRef<BehaviorsMenu, BehaviorsMenuProps>((props, ref) => {
  const currentProject = useCurrentProject();
  const behaviors = useBehaviors();
  const i18n = useTranslation();
  return <BehaviorsMenu ref={ref} currentProject={currentProject} behaviors={behaviors} i18n={i18n} {...props} />;
}), css);